/**
 * Where a notification goes when you click it.
 *
 * The backend stores `entity_type` / `entity_id` rather than a URL, so the
 * route lives here — the API has no business knowing the frontend's paths, and
 * a stored URL would rot the moment a route is renamed.
 *
 * Not every entity has a detail page. `pre_lead` in particular only has list
 * views, so it falls back to the list rather than producing a dead link that
 * renders a blank screen. Anything unmapped returns null and the item renders
 * as plain text, which is the honest outcome — better than navigating somewhere
 * arbitrary and leaving the reader to work out why.
 */

const DETAIL_ROUTES = {
  lead: (id) => `/builder/sales/${id}`,
  deal: (id) => `/builder/deals/${id}`,
  invoice: (id) => `/builder/accounts/invoice/${id}`,
  live_task: (id) => `/builder/operations/managementTasks/${id}`,
  daily_requirement: (id) => `/builder/inventory/requirements/${id}`,
  employee: (id) => `/builder/hr/employees/${id}`,
  project: (id) => `/builder/management/company/projects/${id}`,
  order: (id) => `/builder/inventory/orders/requirement/${id}`
};

/** Entities with no detail page — sent to the nearest list instead. */
const LIST_FALLBACKS = {
  pre_lead: '/builder/sales/prelead-lists/all-leads',
  payment_stage: '/builder/accounts/invoice',
  return_material: '/builder/inventory/requirements',
  leave_request: '/builder/hr/leaves',
  payroll_period: '/builder/hr/payroll',
  rfq: '/builder/inventory/requirements',
  quotation: '/builder/inventory/requirements'
};

export function notificationLink(notification) {
  const type = notification?.entity_type;
  const id = notification?.entity_id;
  if (!type) return null;

  if (id && DETAIL_ROUTES[type]) return DETAIL_ROUTES[type](id);
  if (LIST_FALLBACKS[type]) return LIST_FALLBACKS[type];
  return null;
}

/**
 * Relative time, short enough for a dropdown row.
 *
 * Deliberately not a library: this is the only place the app needs it, and
 * "3h" reads better in a 320px dropdown than "about 3 hours ago".
 */
export function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.floor((Date.now() - then) / 1000);
  // Clock skew between the server and the browser can put a fresh notification
  // slightly in the future; "now" beats "-2s".
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(then).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });
}

/** Tailwind classes for the priority dot. High and above earn a colour. */
export function priorityDotClass(priority) {
  if (priority === 'critical') return 'bg-red-500';
  if (priority === 'high') return 'bg-amber-500';
  return 'bg-transparent';
}
